import { useEffect, useState } from 'react';
import differenceBy            from 'lodash/differenceBy';

import { Socket } from 'socket.io-client';

import { setupSocketConnection }              from '../Header';
import { IFamily }                            from '../../Auth/shared/interfaces';
import { IChat, IMessage, useFetchChatsList } from '../../modules/MessageCenter/queries';
import { useFetchFamilyUser }                 from '../../shared/queries/family';
import { getAuthToken }                       from '../../utils/authHelpers';
import { usePrevious }                        from './usePreviousValue';

interface IChatHubConnectionHookArgs {
  family?          : IFamily;
  onReceiveMessage : (message: IMessage) => void;
}

export const useChatHubConnection = ({
  family,
  onReceiveMessage,
}: IChatHubConnectionHookArgs) => {
  const authToken = getAuthToken();

  const [chatHub, setChatHub] = useState<Socket>();

  const { data: user }      = useFetchFamilyUser(family?.id);
  const { data: chatRooms } = useFetchChatsList(user?.id);

  const prevChatRooms    = usePrevious(chatRooms) ?? [];
  const getLastChatRooms = differenceBy(chatRooms, prevChatRooms, 'id');

  const setupChatHub = () => {
    const socket = setupSocketConnection('hubs/chat', authToken.accessToken);

    setChatHub(socket);
    console.log('Chat hub connection is ready.');
  };

  const joinRooms = async (rooms: IChat[], userId: string) => {
    if (!chatHub) {
      throw new Error('Cannot join rooms, chat hub connection is not found.');
    }

    chatHub.on('ReceiveMessage', (message: IMessage) => onReceiveMessage(message));
    chatHub.on('UserAdded', (message: IMessage) => onReceiveMessage(message));
    chatHub.on('UserDeleted', (message: IMessage) => onReceiveMessage(message));
    console.log('Subscribed on ReceiveMessage, UserAdded, UserDeleted events.');

    chatHub.on('disconnect', () => {
      setChatHub(undefined);
      console.log('Set chat hub connection to undefined.');
    });

    console.log('Launching the chat hub connection.');
    if (!chatHub.connected) {
      await new Promise<void>((resolve) => chatHub.once('connect', resolve));
    }

    rooms.forEach((room) => {
      console.log(`Joining room ${room.id}.`);
      chatHub.emit('JoinRoom', room.id, userId);
    });
    console.log('Rooms are joined.');
  };

  useEffect(() => {
    if (chatRooms?.length && user?.id && !chatHub) {
      setupChatHub();
    }
  }, [chatRooms, user, chatHub]);

  useEffect(() => {
    if (getLastChatRooms?.length && user?.id && chatHub) {
      chatHub.emit('JoinRoom', getLastChatRooms[0].id, user.id);
    }
  }, [getLastChatRooms]);

  useEffect(() => {
    if (chatRooms?.length && user?.id && chatHub) {
      joinRooms(chatRooms, user.id);
    }

    return () => {
      if (chatHub) {
        chatHub.disconnect();
        console.log('Chat hub connection was stopped.');
      }
    };
  }, [chatHub]);
};
